import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'

import { lightTheme } from '~createTheme'

interface AISearchBarProps {
  suggestions?: string[]
  hasResults?: boolean
  onSearch: (query: string) => void
  query: string
  onQueryChange: (query: string) => void
  inputRef?: React.RefObject<HTMLTextAreaElement>
}

const SearchTextField = styled('textarea')({
  backgroundColor: '#FFFFFF',
  fontSize: '18px',
  padding: '20px 20px 20px',
  resize: 'none',
  width: '100%',
  height: '100%',
  border: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  '&:focus': {
    border: `2px solid #ffffff`,
    outline: `2px solid ${lightTheme.palette.primary.main}`,
    outlineOffset: '2px',
    boxShadow: `0 0 0 2px #98e0ff, 0 0 0 4px #98e0ff`,
  },
})

const SuggestionButton = styled('button')({
  borderTop: '1px solid #F0F0F0',
  cursor: 'pointer',
  fontSize: '18px',
  padding: '20px 20px 20px 70px',
  textAlign: 'left',
  width: '100%',
  backgroundColor: 'transparent',
  '&:hover, &:focus': {
    backgroundColor: '#f5f5f5',
  },
})

const AISearchBar: React.FC<AISearchBarProps> = ({
  suggestions = [],
  hasResults = false,
  onSearch,
  query,
  onQueryChange,
  inputRef,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const defaultInputRef = useRef<HTMLTextAreaElement>(null)
  const effectiveInputRef = inputRef || defaultInputRef

  const placeholderText = suggestions.length > 0 ? suggestions[0] : 'Ask a question'
  const remainingSuggestions = suggestions.slice(1)

  const postMessageScrollHeightOneLine = (): void =>
    window.parent.postMessage({ scrollHeight: document.body.scrollHeight }, '*')

  useEffect(() => {
    postMessageScrollHeightOneLine()
  }, [showSuggestions])

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    onQueryChange(event.target.value)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (query.trim()) {
      onSearch(query.trim())
      setShowSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion: string): void => {
    onQueryChange(suggestion)
    onSearch(suggestion)
    setShowSuggestions(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (query.trim()) {
        onSearch(query.trim())
        setShowSuggestions(false)
      }
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Box ref={containerRef}>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          borderLeft: '1px solid #F0F0F0',
          borderRight: '1px solid #F0F0F0',
          borderBottom: '1px solid #F0F0F0',
          justifyContent: 'space-between',
          py: 1,
        }}
      >
        <img
          src='https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/search.svg'
          alt='search-icon'
          style={{
            filter: 'brightness(0)',
            margin: '20px 20px',
            width: '20px',
          }}
        />
        <SearchTextField
          ref={effectiveInputRef}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (!hasResults) {
              setShowSuggestions(true)
            }
          }}
          placeholder={placeholderText}
          rows={1}
          aria-label='search'
          aria-description='search results will appear below'
          role='searchbox'
          tabIndex={0}
        />
        <button
          type='submit'
          className='double-blue-border-on-focus touch-target flex-vh-align'
          style={{
            backgroundColor: '#000000',
            borderRadius: '15px',
            cursor: 'pointer',
            height: '30px',
            margin: '15px 15px',
            minWidth: '30px',
            width: '30px',
            border: 'none',
          }}
          aria-label='submit search'
        >
          <img
            src='https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/forward.svg'
            alt='submit-icon'
            style={{
              filter: 'brightness(0) invert(1)',
              width: '15px',
            }}
          />
        </button>
      </Box>

      {!hasResults && showSuggestions && remainingSuggestions.length > 0 && (
        <Box
          sx={{
            borderBottom: '1px solid #F0F0F0',
            borderLeft: '1px solid #F0F0F0',
            borderRight: '1px solid #F0F0F0',
          }}
        >
          {remainingSuggestions.map((suggestion, index) => (
            <Box key={index}>
              <SuggestionButton tabIndex={0} onClick={() => handleSuggestionClick(suggestion)}>
                {suggestion}
              </SuggestionButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default AISearchBar
